////////////////////////////////////////////////////
// Tiingo crypto adapter.
//
// The app was built against the Deriv/binaryws v3 API, which pushed ready-made
// OHLC candles. Tiingo streams raw trades and top-of-book quotes instead, so
// this module does two jobs:
//
//   1. speaks Tiingo (REST for symbols + history, WebSocket for live ticks)
//   2. re-emits everything in the Deriv message shapes the rest of the app
//      already consumes, so call sites keep reading
//      {msg_type:'ohlc', ohlc:{symbol, open_time, open, high, low, close, granularity}}
//      and Deriv-style candle arrays [{epoch, open, high, low, close}].
//
// Docs: https://www.tiingo.com/documentation/websockets/crypto
////////////////////////////////////////////////////
import axios from 'axios';

// The feed cannot talk to Tiingo directly from a browser: the server answers any
// handshake carrying an Origin header with 403 "Cross origin websockets not
// allowed", and browsers always send one (JS cannot suppress it). Everything
// therefore goes through a relay.
//
// Deployed, that relay is a Vercel Function on this same origin (api/ws.mjs).
// Locally it is the standalone process `npm run serve` starts on port 8100.
// VUE_APP_TIINGO_WS_URL overrides both.
function defaultWsUrl() {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
        const host = window.location.hostname;
        const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
        if (!isLocal) {
            const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${scheme}//${window.location.host}/api/ws`;
        }
    }
    return 'ws://localhost:8100';
}
const WS_URL = process.env.VUE_APP_TIINGO_WS_URL || defaultWsUrl();

// REST is a different story: Tiingo sends no Access-Control-Allow-Origin header,
// so browser XHR straight to api.tiingo.com is blocked. This stays a relative
// path and goes through a same-origin proxy (see devServer in vue.config.js).
// Set VUE_APP_TIINGO_REST_BASE to override when a build is served behind its own
// proxy at a different prefix.
const REST_BASE = process.env.VUE_APP_TIINGO_REST_BASE || '/tiingo/crypto';

// Set VUE_APP_TIINGO_TOKEN in .env.local (already gitignored). Never commit the token.
//
// Leaving it unset is the right choice for a deployed build: Vue CLI inlines
// every VUE_APP_* value into the bundle, so a token set here is readable by
// anyone who loads the page. With no token the app runs in "proxy auth" mode,
// where the REST proxy and the WebSocket relay each attach the token
// server-side from their own environment and it never reaches the browser.
const TOKEN = process.env.VUE_APP_TIINGO_TOKEN || '';

// Tiingo resamples on fixed frequency strings; map the app's granularity seconds.
const RESAMPLE_BY_TF = {
    60: '1min',
    120: '2min',
    180: '3min',
    300: '5min',
    600: '10min',
    900: '15min',
    1800: '30min',
    3600: '1hour',
};

// thresholdLevel 2 = top-of-book AND last trade, 5 = last trade only.
// We take 2 so a quiet pair still shows a moving mid price between trades.
const THRESHOLD_LEVEL = 2;

// Deriv exposed ~50 instruments and the old code subscribed to all of them.
// Tiingo lists thousands of crypto pairs, so track a focused set instead.
// Override with VUE_APP_TIINGO_TICKERS="btcusd,ethusd,..." in .env.local.
const DEFAULT_TICKERS = ['btcusd', 'ethusd', 'solusd', 'xrpusd', 'adausd', 'dogeusd', 'ltcusd', 'linkusd'];

export function configuredTickers() {
    const raw = process.env.VUE_APP_TIINGO_TICKERS || '';
    const list = raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s !== '');

    return list.length ? list : DEFAULT_TICKERS;
}

export function hasToken() {
    return TOKEN !== '';
}

export function resampleFreqFor(tf) {
    return RESAMPLE_BY_TF[tf] || '1min';
}

function isoToEpochSec(iso) {
    return Math.floor(new Date(iso).getTime() / 1000);
}

////////////////////////////// request budget //////////////////////////////

// Tiingo's free tier allows 50 REST requests per hour. The chart code is happy
// to ask far more often than that (it used to refetch every chart every 60s,
// ~240/hour), so every REST call goes through this budget plus the caches below.
//
// The log is persisted: a page reload must not reset the counter, or the real
// quota drains while the local count still looks healthy.
const HOURLY_BUDGET = Number(process.env.VUE_APP_TIINGO_HOURLY_BUDGET || 40);
const BUDGET_KEY = 'tiingo_request_log';
const HOUR_MS = 3600000;

function loadLog() {
    try {
        const raw = window.localStorage.getItem(BUDGET_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch (err) {
        return [];
    }
}

function recentCalls() {
    const cutoff = Date.now() - HOUR_MS;
    return loadLog().filter((t) => typeof t === 'number' && t > cutoff);
}

// How many calls are left in the current rolling hour. Useful for the UI.
export function requestsRemaining() {
    return Math.max(0, HOURLY_BUDGET - recentCalls().length);
}

function spendRequest() {
    const list = recentCalls();
    if (list.length >= HOURLY_BUDGET) {
        const err = new Error(
            `Tiingo request budget reached (${HOURLY_BUDGET}/hour). Holding off rather than draining the quota.`
        );
        err.code = 'BUDGET';
        throw err;
    }
    list.push(Date.now());
    try {
        window.localStorage.setItem(BUDGET_KEY, JSON.stringify(list));
    } catch (err) {
        // Private mode: the budget degrades to per-session, which is still
        // better than not counting at all.
    }
}

////////////////////////////// REST plumbing //////////////////////////////

const memCache = {};
const inFlight = {};

// Every REST call funnels through here so the budget, the response cache and
// the in-flight dedupe all apply uniformly.
async function restGet(path, params, ttlMs) {
    // Cache key deliberately excludes the token.
    const key = path + '?' + JSON.stringify(params);

    const hit = memCache[key];
    if (hit && Date.now() - hit.at < ttlMs) {
        return hit.data;
    }
    // Two charts asking for the same thing at once should cost one request.
    if (inFlight[key]) {
        return inFlight[key];
    }

    spendRequest();
    // In proxy-auth mode there is no local token: the proxy attaches it, so do
    // not send an empty one.
    const query = Object.assign({}, params);
    if (hasToken()) {
        query.token = TOKEN;
    }
    const req = axios
        .get(path, { params: query })
        .then((res) => {
            memCache[key] = { at: Date.now(), data: res.data };
            delete inFlight[key];
            return res.data;
        })
        .catch((err) => {
            delete inFlight[key];
            throw err;
        });

    inFlight[key] = req;
    return req;
}

////////////////////////////// REST: symbols //////////////////////////////

// Replaces Deriv's {active_symbols:'brief'} call. Returns the same field names
// the old active_symbols handler destructured.
const CATALOG_KEY = 'tiingo_catalog_v1';
const CATALOG_TTL_MS = 24 * HOUR_MS;

export async function listSymbols() {
    // The instrument list barely changes, so keep it across reloads: one request
    // a day rather than one per page load.
    try {
        const raw = window.localStorage.getItem(CATALOG_KEY);
        if (raw) {
            const cached = JSON.parse(raw);
            if (cached && Array.isArray(cached.rows) && Date.now() - cached.at < CATALOG_TTL_MS) {
                return cached.rows;
            }
        }
    } catch (err) {
        // Unreadable cache is not fatal; fall through and fetch.
    }

    const data = await restGet(REST_BASE, {}, CATALOG_TTL_MS);
    const rows = (Array.isArray(data) ? data : [])
        .filter((el) => el && el.ticker)
        .map((el) => ({
            symbol: el.ticker,
            display_name: el.name || String(el.ticker).toUpperCase(),
            market_display_name: 'Crypto',
            symbol_type: 'crypto',
            submarket_display_name: el.quoteCurrency ? String(el.quoteCurrency).toUpperCase() : 'Crypto',
        }));

    try {
        window.localStorage.setItem(CATALOG_KEY, JSON.stringify({ at: Date.now(), rows: rows }));
    } catch (err) {
        // Private mode or quota: the in-memory cache still applies this session.
    }

    return rows;
}

////////////////////////////// REST: history //////////////////////////////

// Replaces Deriv's {ticks_history, style:'candles'} one-shot call.
// Returns Deriv-shaped candles: [{epoch, open, high, low, close}].
export async function fetchCandles(symbol, tf, count, endMs) {
    const bucketMs = tf * 1000;
    // Quantise the window to the bar boundary. Without this every call produces
    // a slightly different endDate, so the cache key never repeats and each
    // repaint costs a request against the hourly budget.
    const rawEnd = endMs && endMs != 0 ? endMs : Date.now();
    const endDate = new Date(Math.floor(rawEnd / bucketMs) * bucketMs);
    // Tiingo selects by date range rather than bar count, so walk back far enough
    // to cover `count` bars and trim the tail afterwards. The padding absorbs
    // gaps from illiquid pairs.
    const startDate = new Date(endDate.getTime() - count * bucketMs * 1.5);

    const data = await restGet(
        `${REST_BASE}/prices`,
        {
            tickers: symbol,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            resampleFreq: resampleFreqFor(tf),
        },
        // A bar cannot change until the next one opens.
        bucketMs
    );

    const body = Array.isArray(data) ? data : [];
    if (body.length < 1 || !Array.isArray(body[0].priceData)) {
        return [];
    }

    const candles = body[0].priceData.map((row) => ({
        epoch: isoToEpochSec(row.date),
        open: Number(row.open),
        high: Number(row.high),
        low: Number(row.low),
        close: Number(row.close),
    }));

    return candles.slice(-count);
}

////////////////////////////// WebSocket: live //////////////////////////////

// One socket for the whole app. The engine, the chart view and the login
// backdrop each ask for a feed; opening one socket each meant three upstream
// Tiingo connections and three independent retry loops racing each other.
// createLiveFeed() hands out a handle onto this single shared connection: the
// subscriber list is merged, so the socket carries the union of what everyone
// asked for and each caller still gets its own callback.
let sharedFeed = null;

function makeSharedFeed() {
    const subscribers = [];
    // buckets[`${ticker}|${tf}`] = {epoch, open, high, low, close}
    const buckets = {};
    const BASE_MS = 1000;
    const MAX_MS = 30000;

    let socket;
    let stopped = false;
    let attempts = 0;
    let timer = null;
    let warnedUnreachable = false;
    let subscribedKey = '';

    function activeSubs() {
        return subscribers.filter((s) => s.active);
    }

    function unionTickers() {
        const seen = {};
        activeSubs().forEach((s) => {
            s.tickers.forEach((t) => {
                seen[t] = true;
            });
        });
        return Object.keys(seen).sort();
    }

    function unionGranularities() {
        const seen = {};
        activeSubs().forEach((s) => {
            s.granularities.forEach((g) => {
                seen[g] = true;
            });
        });
        const list = Object.keys(seen).map(Number);
        return list.length ? list : [60];
    }

    // One misbehaving handler must not stop the others from getting data.
    function emit(msg) {
        activeSubs().forEach((s) => {
            try {
                s.onOhlc(msg);
            } catch (err) {
                console.error('[tiingo] feed handler threw:', err.message);
            }
        });
    }

    function emitFatal(err) {
        activeSubs().forEach((s) => {
            try {
                s.onFatal(err);
            } catch (e) {
                /* ignore */
            }
        });
    }

    function delay() {
        const ceiling = Math.min(BASE_MS * Math.pow(2, attempts), MAX_MS);
        return Math.round(ceiling / 2 + Math.random() * (ceiling / 2));
    }

    function scheduleReconnect() {
        // Single-flight: never let more than one attempt be pending.
        if (stopped || timer != null || activeSubs().length < 1) {
            return;
        }
        const wait = delay();
        attempts += 1;
        timer = setTimeout(function () {
            timer = null;
            connect();
        }, wait);
    }

    // Folds one price point into every tracked granularity and emits the result.
    function applyPrice(ticker, tsMs, price) {
        if (!isFinite(price)) {
            return;
        }
        const tsSec = Math.floor(tsMs / 1000);

        unionGranularities().forEach(function (tf) {
            const key = `${ticker}|${tf}`;
            const openTime = Math.floor(tsSec / tf) * tf;
            let b = buckets[key];

            if (!b || b.epoch !== openTime) {
                b = { epoch: openTime, open: price, high: price, low: price, close: price };
                buckets[key] = b;
            } else {
                b.close = price;
                if (price > b.high) b.high = price;
                if (price < b.low) b.low = price;
            }

            emit({
                msg_type: 'ohlc',
                ohlc: {
                    symbol: ticker,
                    open_time: b.epoch,
                    open: b.open,
                    high: b.high,
                    low: b.low,
                    close: b.close,
                    granularity: tf,
                },
            });
        });
    }

    function handleData(row) {
        if (!Array.isArray(row) || row.length < 3) {
            return;
        }
        const kind = row[0];
        const ticker = String(row[1]).toLowerCase();
        const tsMs = new Date(row[2]).getTime();
        if (!isFinite(tsMs)) {
            return;
        }

        if (kind === 'T') {
            // ["T", ticker, date, exchange, lastSize, lastPrice]
            applyPrice(ticker, tsMs, Number(row[5]));
        } else if (kind === 'Q') {
            // ["Q", ticker, date, exchange, bidSize, bidPrice, midPrice, askSize, askPrice]
            const mid = Number(row[6]);
            // Mid is null on one-sided books; fall back to whichever side quoted.
            const price = isFinite(mid) && mid > 0 ? mid : Number(row[5]) || Number(row[8]);
            applyPrice(ticker, tsMs, Number(price));
        }
    }

    function connect() {
        if (stopped || timer != null) {
            return;
        }
        if (socket != undefined) {
            // CONNECTING / CLOSING: an attempt is in flight or onclose will requeue.
            if (socket.readyState == 0 || socket.readyState == 2) {
                return;
            }
            if (socket.readyState == 1) {
                return;
            }
        }
        const tickers = unionTickers();
        if (tickers.length < 1) {
            // Nothing to subscribe to yet; a later start()/setTickers() re-arms.
            return;
        }

        socket = new WebSocket(WS_URL);

        socket.onopen = function () {
            warnedUnreachable = false;
            subscribedKey = unionTickers().join(',');
            // In proxy-auth mode this is empty and the relay substitutes its own
            // token before forwarding the frame upstream.
            socket.send(
                JSON.stringify({
                    eventName: 'subscribe',
                    authorization: TOKEN,
                    eventData: { thresholdLevel: THRESHOLD_LEVEL, tickers: unionTickers() },
                })
            );
        };

        socket.onmessage = function (msg) {
            let payload;
            try {
                payload = JSON.parse(msg.data);
            } catch (err) {
                return;
            }

            // 'E' error, 'I' subscription info, 'H' heartbeat, 'A' data.
            if (payload.messageType === 'E') {
                const code = payload.response ? payload.response.code : 0;
                const text = payload.response ? payload.response.message : 'unknown error';
                if (code === 401 || code === 403) {
                    // A rejected token never recovers, so stop instead of looping.
                    stopped = true;
                    emitFatal(new Error(`Tiingo auth failed (${code}): ${text}`));
                    return;
                }
                console.error(`[tiingo] feed error ${code}: ${text}`);
                return;
            }
            if (payload.messageType === 'A' && payload.data) {
                // Live data means the connection is healthy: reset the backoff.
                attempts = 0;
                handleData(payload.data);
            }
        };

        socket.onerror = function () {
            // No reconnect here: a failed socket fires 'error' and then 'close',
            // so reconnecting from both would double the attempts every round.
            if (!warnedUnreachable) {
                warnedUnreachable = true;
                console.error(
                    `[tiingo] cannot reach the live feed at ${WS_URL}. ` +
                        'Tiingo refuses WebSocket handshakes from a browser, so the app needs ' +
                        'the local relay. Start it in a second terminal with:  npm run ws-relay'
                );
            }
        };

        socket.onclose = function () {
            subscribedKey = '';
            scheduleReconnect();
        };
    }

    // Brings the socket in line with whatever the active subscribers now want.
    function refresh() {
        if (activeSubs().length < 1) {
            stopSocket();
            return;
        }
        stopped = false;
        if (socket != undefined && socket.readyState == 1) {
            // Tiingo binds the ticker set at subscribe time, so a changed union
            // means reconnecting; an unchanged one costs nothing.
            if (unionTickers().join(',') !== subscribedKey) {
                socket.close();
            }
            return;
        }
        connect();
    }

    function stopSocket() {
        stopped = true;
        if (timer != null) {
            clearTimeout(timer);
            timer = null;
        }
        if (socket != undefined && socket.readyState != 3) {
            socket.close();
        }
    }

    function normalise(list) {
        return (list || []).map((t) => String(t).toLowerCase());
    }

    return {
        addSubscriber(options) {
            const opts = options || {};
            const sub = {
                active: false,
                tickers: normalise(opts.tickers),
                granularities: opts.granularities && opts.granularities.length ? opts.granularities : [60],
                onOhlc: opts.onOhlc || function () {},
                onFatal: opts.onFatal || function () {},
            };
            subscribers.push(sub);

            return {
                start() {
                    sub.active = true;
                    refresh();
                },
                stop() {
                    sub.active = false;
                    refresh();
                },
                setTickers(next) {
                    sub.tickers = normalise(next);
                    refresh();
                },
                setGranularities(next) {
                    sub.granularities = next && next.length ? next : [60];
                    refresh();
                },
                currentCandle(ticker, tf) {
                    return buckets[`${String(ticker).toLowerCase()}|${tf}`];
                },
            };
        },
    };
}

export function createLiveFeed(options) {
    if (sharedFeed == null) {
        sharedFeed = makeSharedFeed();
    }
    return sharedFeed.addSubscriber(options);
}
